import React from 'react';
import Layout from '../components/Layout';
import { useLang } from '../i18n';
import Iso9001 from '../images/iso_9001.jpg';
import Iso14001 from '../images/iso_14001.jpg';
import Iso45001 from '../images/iso_45001.jpg';

const CERTIFICATES = [
    { image: Iso9001, title: 'ISO 9001:2015' },
    { image: Iso14001, title: 'ISO 14001:2015' },
    { image: Iso45001, title: 'ISO 45001:2018' },
];

const CONTENT = {
    tr: {
        title: 'Şirket',
        description: 'AKKA Demir Çelik hakkında: misyonumuz, vizyonumuz ve ISO 9001, ISO 14001, ISO 45001 sertifikalarımız.',
        heading: 'Hakkımızda',
        about: 'Firmamız kuruluş itibarı ile sektörde bulunan nitelikli ürün üretme ve çözüm odaklı çalışma prensibine yeni bir bakış açısı kazandırmak istemektedir. Zamanın ve hammaddenin değerinin farkında olarak lazer kesim, abkant büküm, kaynaklı imalat, proje bazlı işler ile tasarım alanında hem yurtiçi hem de yurtdışı hizmet vermek amacı ile hareket etmektedir.',
        mission: 'Misyon',
        missionText: 'Kaliteli iş ve ürün üretme ilkesi ile hareket eden AKKA Demir Çelik, taahhüt ettiği projeleri zamanında ve eksiksiz teslim etme misyonuna sahiptir. Ulusal sanayi sektörünün dışa bağımlılığını azaltma çabası içerisinde olarak, çözüm odaklı proje ve yaklaşımı ile sektöre yeni bir bakış açısı kazandırmayı amaç edinmektedir.',
        vision: 'Vizyon',
        visionText: 'AKKA Demir Çelik son teknoloji ile tecrübeyi harmanlayarak ulusal ve uluslararası tüm pazarlarda var olmayı hedefler. Bu doğrultuda kesintisiz imalat, verimli çalışma ortamı ve teknoloji desteği ile katma değeri yüksek bir sanayi kuruluşu olma vizyonunu ilke edinmektedir.',
        certificates: 'Sertifikalarımız',
        certificateNames: ['Kalite Yönetim Sistemi', 'Çevre Yönetim Sistemi', 'İş Sağlığı ve Güvenliği Yönetim Sistemi'],
        certificateAlt: (title, name) => `${title} ${name} sertifikası`,
    },
    en: {
        title: 'Company',
        description: 'About AKKA Demir Çelik: our mission, our vision and our ISO 9001, ISO 14001 and ISO 45001 certificates.',
        heading: 'About Us',
        about: 'Since its founding, our company has set out to bring a fresh perspective to producing quality products and working in a solution-focused way. Aware of the value of time and raw materials, we provide laser cutting, press brake bending, welded fabrication, project-based work and design services in Türkiye and abroad.',
        mission: 'Mission',
        missionText: 'Guided by the principle of producing quality work and products, AKKA Demir Çelik’s mission is to deliver every project it takes on completely and on time. As part of the effort to reduce national industry’s dependence on imports, it aims to bring a new perspective to the sector through solution-focused projects and thinking.',
        vision: 'Vision',
        visionText: 'AKKA Demir Çelik aims to be present in all national and international markets by combining the latest technology with experience. To that end, its vision is to become a high value-added industrial company through uninterrupted production, an efficient working environment and technology.',
        certificates: 'Our Certificates',
        certificateNames: ['Quality Management System', 'Environmental Management System', 'Occupational Health and Safety Management System'],
        certificateAlt: (title, name) => `${title} ${name} certificate`,
    },    de: {
        title: 'Unternehmen',
        description: 'Über AKKA Demir Çelik: unsere Mission, unsere Vision und unsere Zertifikate nach ISO 9001, ISO 14001 und ISO 45001.',
        heading: 'Über uns',
        about: 'Seit unserer Gründung wollen wir der Branche einen neuen Blick auf Qualitätsfertigung und lösungsorientiertes Arbeiten geben. Im Bewusstsein, wie wertvoll Zeit und Rohstoffe sind, bieten wir Laserschneiden, Abkanten, Schweißbaugruppen, projektbezogene Fertigung und Konstruktion für Kunden in der Türkei und im Ausland an.',
        mission: 'Mission',
        missionText: 'AKKA Demir Çelik arbeitet nach dem Grundsatz, hochwertige Arbeit und Produkte zu liefern. Unsere Mission ist es, jedes übernommene Projekt vollständig und pünktlich zu liefern. Als Beitrag dazu, die Importabhängigkeit der türkischen Industrie zu verringern, wollen wir der Branche mit lösungsorientierten Projekten und Denkweisen neue Impulse geben.',
        vision: 'Vision',
        visionText: 'AKKA Demir Çelik verbindet modernste Technik mit Erfahrung und will auf allen nationalen und internationalen Märkten präsent sein. Unsere Vision ist es, durch unterbrechungsfreie Fertigung, ein effizientes Arbeitsumfeld und technologische Unterstützung ein Industrieunternehmen mit hoher Wertschöpfung zu werden.',
        certificates: 'Unsere Zertifikate',
        certificateNames: ['Qualitätsmanagementsystem', 'Umweltmanagementsystem', 'Managementsystem für Sicherheit und Gesundheit bei der Arbeit'],
        certificateAlt: (title, name) => `Zertifikat ${title} ${name}`,
    },
};

const Section = ({ title, children }) => (
    <section className="mb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-0 mb-4">{title}</h2>
        <p className="text-lg text-gray-700 leading-relaxed">{children}</p>
    </section>
);

const Company = () => {
    const lang = useLang();
    const c = CONTENT[lang];

    return (
        <Layout title={c.title} description={c.description}>
            <div className="bg-white">
                <div className="container mx-auto px-4 py-12 max-w-5xl">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-0 mb-2">{c.heading}</h1>
                    <div className="w-24 h-1 bg-blue-600 rounded mb-8"></div>

                    <p className="text-lg text-gray-700 leading-relaxed mb-12">{c.about}</p>

                    <Section title={c.mission}>{c.missionText}</Section>
                    <Section title={c.vision}>{c.visionText}</Section>

                    <section id="sertifikalar" className="scroll-mt-20">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-0 mb-6">{c.certificates}</h2>
                        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                            {CERTIFICATES.map(({ image, title }, i) => (
                                <a
                                    key={title}
                                    href={image}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group block rounded-xl border border-gray-200 bg-gray-50 p-4 text-center hover:shadow-lg transition-shadow"
                                >
                                    <img
                                        src={image}
                                        alt={c.certificateAlt(title, c.certificateNames[i])}
                                        loading="lazy"
                                        className="w-full rounded-md border border-gray-200"
                                    />
                                    <span className="block mt-4 font-bold text-gray-900">{title}</span>
                                    <span className="block text-sm text-gray-600">{c.certificateNames[i]}</span>
                                </a>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </Layout>
    );
};

export default Company;
